import React from "react";
import { Modal, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  GeoAlt,
  InfoCircle,
  SendFill,
  Person,
  Building,
  TrashFill,
  Pen,
  Plus,
  BoxArrowUpRight,
  ExclamationCircleFill,
} from "react-bootstrap-icons";
import moment from "moment-timezone";
import { navigateToAppointment, formatHouseType } from "../utils/mapUtils";
import "./JobPopover.css";

const AGENT_VISIBILITY = {
  HIDDEN: "hidden",
  WARNING: "warning",
  VISIBLE: "visible",
};

const JobPopover = ({
  show,
  onHide,
  appointment,
  onDelete,
  onJobBooking,
  onJobSetup,
  loading = false,
}) => {
  // Early return if no appointment
  if (!appointment) return null;

  // Extract status from appointment
  const status = appointment?.tag || appointment?.status || "booked";

  // Get status color and display text
  const getStatusInfo = (status) => {
    const normalizedStatus = status?.toLowerCase();
    switch (normalizedStatus) {
      case "booked":
        return { color: "#FFC107", text: "Booked" };
      case "pre-inspection":
        return { color: "#0DCAF0", text: "Pre-Inspection" };
      case "under-inspection":
        return { color: "#80B2FF", text: "Under Inspection" };
      case "completed":
        return { color: "#DEE2E6", text: "Completed" };
      default:
        return { color: "#FFC107", text: "Booked" };
    }
  };

  const statusInfo = getStatusInfo(status);

  // Check if it's mobile and truncate status text if needed
  const isMobile = window.innerWidth <= 480;
  const displayStatusText =
    isMobile && statusInfo.text.length > 6
      ? statusInfo.text.substring(0, 3) + "..."
      : statusInfo.text;

  // Format time display
  const formatTimeDisplay = () => {
    if (!appointment) return "";
    try {
      const startTime = moment(
        (appointment.start_time || appointment.start).substring(0, 19)
      );
      const endTime = moment(
        (appointment.end_time || appointment.end).substring(0, 19)
      );
      return `${startTime.format("MMM D, h:mm A")} - ${endTime.format("h:mm A")}`;
    } catch (error) {
      console.error("Error formatting time:", error);
      return "Time unavailable";
    }
  };

  // Get address display
  const getAddressDisplay = () => {
    if (!appointment) return "";

    const reportAddress = appointment.job?.report?.address;

    if (reportAddress && typeof reportAddress === "object") {
      const addressParts = [
        reportAddress.address_line1,
        reportAddress.address_line2,
        reportAddress.city,
        reportAddress.state,
        reportAddress.zip_code,
      ].filter(Boolean);
      return addressParts.join(", ");
    } else if (typeof reportAddress === "string") {
      return reportAddress;
    }

    return "";
  };

  // Get property details
  const getPropertyDetails = () => {
    if (!appointment) return {};

    const report = appointment.job?.report || {};

    return {
      yearBuilt: report.year_build || "",
      squareFeet: report.floor_area || "",
      numberOfStories: report.stories || "",
      houseType: report.house_type || "",
      parcelNumber: report.apn || "",
      note: report.info || "",
    };
  };

  // Get client information
  const getClientInfo = () => {
    if (!appointment) return {};

    const client = appointment.job?.report?.client || {};

    return {
      name: client.name || "",
      phone: client.phone || "",
      email: client.email || "",
      note: client.note || "",
    };
  };

  // Get agent information
  const getAgentInfo = () => {
    if (!appointment) return {};

    const agent = appointment.job?.report?.agent || {};

    return {
      id: agent.id || "",
      name: agent.name || "",
      phone: agent.phone || "",
      email: agent.email || "",
      role: agent.role || "",
      note: agent.note || "",
    };
  };

  // Determine agent section visibility
  const getAgentDisplayState = () => {
    const report = appointment?.job?.report;
    const agentApplicable = report?.agent_applicable;
    const agentData = report?.agent;

    if (agentApplicable === false) return AGENT_VISIBILITY.HIDDEN;
    if (agentApplicable === true && !agentData) return AGENT_VISIBILITY.WARNING;
    if (agentData) return AGENT_VISIBILITY.VISIBLE;
    return AGENT_VISIBILITY.HIDDEN;
  };

  // Compute display data
  const timeDisplay = formatTimeDisplay();
  const addressDisplay = getAddressDisplay();
  const propertyDetails = getPropertyDetails();
  const clientInfo = getClientInfo();
  const agentInfo = getAgentInfo();
  const agentDisplayState = getAgentDisplayState();

  // Check if any property details exist
  const hasAnyPropertyDetails = () => {
    return (
      propertyDetails.yearBuilt ||
      propertyDetails.squareFeet ||
      propertyDetails.numberOfStories ||
      propertyDetails.houseType ||
      propertyDetails.parcelNumber ||
      propertyDetails.note
    );
  };

  return (
    <Modal show={show} onHide={onHide} centered className="job-popover-modal">
      <Modal.Header closeButton={false} className="job-popover-header">
        <div className="job-popover-time">
          <span>{timeDisplay}</span>
          {appointment.note && (
            <OverlayTrigger
              placement="bottom"
              delay={{ show: 250, hide: 400 }}
              overlay={
                <Tooltip
                  id="appointment-note-tooltip"
                  className="custom-tooltip"
                >
                  {appointment.note}
                </Tooltip>
              }
            >
              <span>
                <InfoCircle size={16} className="job-popover-time-info" />
              </span>
            </OverlayTrigger>
          )}
        </div>
        <div
          className="job-status-badge"
          style={{ backgroundColor: statusInfo.color }}
        >
          {displayStatusText}
        </div>
      </Modal.Header>

      <Modal.Body className="job-popover-body">
        {loading ? (
          <div className="job-popover-loading">
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: "200px" }}
            >
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">
                  Loading appointment details...
                </span>
              </div>
              <span className="ms-3">Loading appointment details...</span>
            </div>
          </div>
        ) : (
          <>
            {/* Address Section */}
            <div className="job-popover-section">
              <div className="job-popover-address">
                <GeoAlt className="job-popover-location-icon" />
                <span className="job-popover-address-text">
                  {addressDisplay}
                </span>
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="job-popover-nav-button"
                  onClick={() => navigateToAppointment(appointment)}
                >
                  <SendFill size={16} />
                </Button>
              </div>
            </div>

            {/* Property Details */}
            {hasAnyPropertyDetails() && (
              <div className="job-popover-section">
                <div className="job-popover-property-details">
                  <div className="popover-property-grid">
                    {propertyDetails.yearBuilt && (
                      <div className="popover-property-field">
                        <span className="popover-property-label">
                          Year Built
                        </span>
                        <span className="popover-property-value">
                          {propertyDetails.yearBuilt}
                        </span>
                      </div>
                    )}
                    {propertyDetails.squareFeet && (
                      <div className="popover-property-field">
                        <span className="popover-property-label">
                          Square Feet
                        </span>
                        <span className="popover-property-value">
                          {propertyDetails.squareFeet}
                        </span>
                      </div>
                    )}
                    {propertyDetails.numberOfStories && (
                      <div className="popover-property-field">
                        <span className="popover-property-label">
                          Number of Stories
                        </span>
                        <span className="popover-property-value">
                          {propertyDetails.numberOfStories}
                        </span>
                      </div>
                    )}
                    {propertyDetails.houseType && (
                      <div className="popover-property-field">
                        <span className="popover-property-label">
                          House Type
                        </span>
                        <span className="popover-property-value">
                          {formatHouseType(propertyDetails.houseType)}
                        </span>
                      </div>
                    )}
                    {propertyDetails.parcelNumber && (
                      <div className="popover-property-field">
                        <span className="popover-property-label">
                          Parcel Number
                        </span>
                        <span className="popover-property-value">
                          {propertyDetails.parcelNumber}
                        </span>
                      </div>
                    )}
                    {propertyDetails.note && (
                      <div className="popover-property-field">
                        <span className="popover-property-label">Note</span>
                        <span className="popover-property-value">
                          {propertyDetails.note}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Client Information */}
            {clientInfo.name && (
              <div className="job-popover-section">
                <div className="job-popover-contact">
                  <div className="contact-header">
                    <Person className="contact-icon" />
                    <span className="contact-name">{clientInfo.name}</span>
                    {clientInfo.note && (
                      <OverlayTrigger
                        placement="bottom"
                        delay={{ show: 250, hide: 400 }}
                        overlay={
                          <Tooltip
                            id="client-note-tooltip"
                            className="custom-tooltip"
                          >
                            {clientInfo.note}
                          </Tooltip>
                        }
                      >
                        <span>
                          <InfoCircle size={16} className="contact-info-icon" />
                        </span>
                      </OverlayTrigger>
                    )}
                  </div>
                  <div className="contact-details">
                    {clientInfo.phone && (
                      <div className="contact-detail">{clientInfo.phone}</div>
                    )}
                    {clientInfo.email && (
                      <div className="contact-detail">{clientInfo.email}</div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Agent Information */}
            {agentDisplayState !== AGENT_VISIBILITY.HIDDEN && (
              <div className="job-popover-section">
                <div className="job-popover-contact">
                  {agentDisplayState === AGENT_VISIBILITY.VISIBLE ? (
                    <>
                      <div className="contact-header">
                        <Building className="contact-icon" />
                        <span className="contact-name">{agentInfo.name}</span>
                        <BoxArrowUpRight
                          size={16}
                          className="contact-info-icon"
                          style={{ color: "#6C757D", cursor: "pointer" }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (agentInfo.id) {
                              window.open(
                                `/agent-management/${agentInfo.id}/edit`,
                                "_blank"
                              );
                            }
                          }}
                        />
                        {agentInfo.note && (
                          <OverlayTrigger
                            placement="bottom"
                            delay={{ show: 250, hide: 400 }}
                            overlay={
                              <Tooltip
                                id="agent-note-tooltip"
                                className="custom-tooltip"
                              >
                                {agentInfo.note}
                              </Tooltip>
                            }
                          >
                            <span>
                              <InfoCircle
                                size={16}
                                className="contact-info-icon"
                              />
                            </span>
                          </OverlayTrigger>
                        )}
                        {agentInfo.role && (
                          <span className="contact-role">
                            ({agentInfo.role})
                          </span>
                        )}
                      </div>
                      <div className="contact-details">
                        {agentInfo.phone && (
                          <div className="contact-detail">
                            {agentInfo.phone}
                          </div>
                        )}
                        {agentInfo.email && (
                          <div className="contact-detail">
                            {agentInfo.email}
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="contact-header">
                      <Building className="contact-icon" />
                      <span className="no-quote" style={{ color: "#CC9A06" }}>
                        Agent not assigned
                        <ExclamationCircleFill
                          size={14}
                          style={{ marginLeft: "6px" }}
                        />
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Job Information */}
            {status.toLowerCase() !== "booked" && (
              <div className="job-popover-section job-section">
                <div className="job-section-divider"></div>
                <div className="job-info">
                  <div className="job-info-row">
                    <span className="job-info-label">Section Template</span>
                    <span className="job-info-value">
                      {appointment.job?.template?.name || "Standard Sections"}
                    </span>
                  </div>
                  <div className="job-info-row">
                    <span className="job-info-label">Quote</span>
                    <span className="job-info-value">
                      {appointment.job?.quote ? (
                        <span>
                          {appointment.job.quote}
                          {appointment.job?.note && (
                            <OverlayTrigger
                              placement="bottom"
                              delay={{ show: 250, hide: 400 }}
                              overlay={
                                <Tooltip
                                  id="job-note-tooltip"
                                  className="custom-tooltip"
                                >
                                  {appointment.job.note}
                                </Tooltip>
                              }
                            >
                              <span>
                                <InfoCircle
                                  size={16}
                                  style={{
                                    marginLeft: "6px",
                                    color: "#0D6EFD",
                                    cursor: "pointer",
                                  }}
                                />
                              </span>
                            </OverlayTrigger>
                          )}
                        </span>
                      ) : (
                        <span>
                          <span
                            className="no-quote"
                            style={{ color: "#CC9A06" }}
                          >
                            No quote yet
                            <ExclamationCircleFill
                              size={14}
                              style={{ marginLeft: "6px" }}
                            />
                          </span>
                          {appointment.job?.note && (
                            <OverlayTrigger
                              placement="bottom"
                              delay={{ show: 250, hide: 400 }}
                              overlay={
                                <Tooltip
                                  id="job-note-tooltip"
                                  className="custom-tooltip"
                                >
                                  {appointment.job.note}
                                </Tooltip>
                              }
                            >
                              <span>
                                <InfoCircle
                                  size={16}
                                  style={{
                                    marginLeft: "6px",
                                    color: "#0D6EFD",
                                    cursor: "pointer",
                                  }}
                                />
                              </span>
                            </OverlayTrigger>
                          )}
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Report Section - Only for completed status */}
            {status.toLowerCase() === "completed" &&
              appointment.job?.report?.compiled_report?.created_at && (
                <div className="job-popover-section job-report-section">
                  <div className="job-section-divider"></div>
                  <div className="job-info">
                    <div className="job-info-row">
                      <span className="job-info-label">Report</span>
                      <span className="job-info-value">
                        {(() => {
                          const createdAt =
                            appointment.job.report.compiled_report.created_at;
                          const date = moment(createdAt);
                          return (
                            date.format("MM/DD/YYYY HH:mm:ss") + " compiled"
                          );
                        })()}
                        <BoxArrowUpRight
                          size={16}
                          style={{
                            marginLeft: "6px",
                            color: "#6C757D",
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            const baseUrl =
                              process.env.REACT_APP_URL ||
                              "http://localhost:8000";
                            const reportId =
                              appointment.job.report.compiled_report.id;
                            window.open(
                              `${baseUrl}/compiled-reports/${reportId}/`,
                              "_blank"
                            );
                          }}
                        />
                      </span>
                    </div>
                  </div>
                </div>
              )}
          </>
        )}
      </Modal.Body>

      {/* Footer with action buttons */}
      <Modal.Footer className="job-popover-footer">
        <Button
          variant="outline-danger"
          size="sm"
          className="job-popover-delete-btn"
          onClick={() => onDelete && onDelete(appointment)}
          disabled={loading}
        >
          <TrashFill size={40} />
        </Button>

        <div className="job-popover-footer-actions">
          <Button
            variant="outline-secondary"
            size="sm"
            className="job-popover-booking-btn"
            onClick={() => onJobBooking && onJobBooking(appointment)}
            disabled={loading}
          >
            <Pen size={14} />
            Job Booking
          </Button>

          <Button
            variant={
              status.toLowerCase() === "booked"
                ? "primary"
                : "outline-secondary"
            }
            size="sm"
            className="job-popover-setup-btn"
            onClick={() => onJobSetup && onJobSetup(appointment)}
            disabled={loading}
          >
            {status.toLowerCase() === "booked" ? (
              <Plus size={24} />
            ) : (
              <Pen size={14} />
            )}
            Job Setup
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default JobPopover;
